import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";

// UI components
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericSearchbar, {
  ParsedFilter,
} from "@ui-components/Searchbar/GenericSearchbar";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  SkeletonTableRows,
  SkeletonLine,
} from "@ui-components/Skeleton/GenericSkeleton";
import { DataStateView, ErrorStateView } from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
} from "kontrolEdge/helpers/advancedFilters";

// Data and types
import {
  getAvailableColumns,
  getSelectedColumns,
  makeSyslogFields,
} from "./SyslogData";
import {
  getCTXRlistGlobal,
  getAvailableSyslogs,
  getSelectedSyslogs,
  saveSyslogs,
  SyslogItem,
  // V2 API functions
  getAvailableSyslogServersV2,
  getSelectedSyslogsV2ForInstance,
  getSelectedSyslogsV2ForCluster,
  createSyslogAssociationForInstance,
  createSyslogAssociationForCluster,
  deleteSyslogAssociationV2,
  updateSyslogAssociation,
} from "./request/request";
import { useAppSelector } from "@redux/hooks";
import {
  getTableBodyMaxHeight,
  getInitialRowsPerPage,
  REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS,
} from "../../../helpers/tableViewport";

/** Dual pickers share leftover height under the searchbars and Save row (CTX-331). */
const SYSLOG_PICKER_CHROME_PX = 420;

interface CtxrItem {
  id: string;
  name: string;
  description: string;
  subscriptionId: number;
}

/**
 * Interface for raw syslog server data from V2 API
 */
interface SyslogServerResponse {
  id: number;
  ip: string;
  port: number;
  description: string;
  protocol?: "udp" | "tcp";
  reflectorInstanceCount?: number;
  level?: number;
  syslogServerId?: number; // For association responses
}

/**
 * Interface for syslog association response from V2 API
 * Note: Response structure differs between cluster and standalone modes:
 * - Cluster mode: has `associationIds` (array) and `reflectorInstances` (array)
 * - Standalone mode: has `id` (single number) and `reflectorInstance` (single object)
 */
interface SyslogAssociationResponse {
  id?: number; // Single association ID (standalone mode)
  associationIds?: number[]; // Array of association IDs (cluster mode)
  syslogServer: {
    id: number;
    ip: string;
    port: number;
    protocol: "udp" | "tcp";
    description: string;
  };
  cluster?: {
    id: number;
    name: string;
  };
  reflectorInstance?: {
    id: number;
    name: string;
    clusterId: number | null;
    clusterName: string | null;
  };
  reflectorInstances?: Array<{
    id: number;
    name: string;
  }>;
  level: number;
  propagationMode: "cluster" | "standalone";
}

interface SyslogPageProps {
  /**
   * CTX-R instance ID (reflector ID) - if provided, selector won't be shown
   */
  ctxrId?: string;
  /**
   * Cluster ID (if in cluster context)
   */
  clusterId?: number;
  /**
   * Function to get access token
   */
  getAccessTokenSilently: () => Promise<string>;
}

function SyslogPage({
  ctxrId: propCtxrId,
  clusterId,
  getAccessTokenSilently,
}: SyslogPageProps) {
  const { isAuthenticated, isLoading } = useAuth0();
  const [isFetching, setIsFetching] = useState(false);
  const [syslogError, setSyslogError] = useState<string | null>(null);
  const { tenantId } = useAppSelector((state) => state.selection);
  const isLoadingDataRef = useRef(false);

  // CTX-R instance selector (only used when ctxrId prop is not provided)
  const [fullCTXRlist, setFullCTXRlist] = useState<CtxrItem[]>([]);
  const [instanceOptions, setInstanceOptions] = useState<string[]>([]);
  const [selectedInstance, setSelectedInstance] = useState("");
  const [currentCtxrId, setCurrentCtxrId] = useState<string | undefined>(propCtxrId);

  // Syslog items
  const [availableItems, setAvailableItems] = useState<SyslogItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<SyslogItem[]>([]);

  // Initial copy (for Undo)
  const [initialAvailable, setInitialAvailable] = useState<SyslogItem[]>([]);
  const [initialSelected, setInitialSelected] = useState<SyslogItem[]>([]);

  // Checkboxes
  const [selectedAvailableIds, setSelectedAvailableIds] = useState<Set<number>>(
    new Set()
  );
  const [selectedSelectedIds, setSelectedSelectedIds] = useState<Set<number>>(
    new Set()
  );

  // Detect changes => Undo / Save
  const [hasChanges, setHasChanges] = useState(false);

  // Filters for Available and Selected with GenericSearchbar
  const [activeFiltersAvailable, setActiveFiltersAvailable] = useState<
    ParsedFilter[]
  >([]);
  const [activeFiltersSelected, setActiveFiltersSelected] = useState<
    ParsedFilter[]
  >([]);
  const syslogFields = useMemo(() => makeSyslogFields(), []);

  // Generate suggested keys from field labels
  const suggestedKeys = useMemo(() => {
    return syslogFields.map((f) => f.label);
  }, [syslogFields]);

  // Build maps for filter normalization (Available)
  const fieldByIdAvailable = useMemo(() => {
    const m = new Map<string, FilterField<SyslogItem>>();
    for (const f of syslogFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [syslogFields]);

  const keyMapAvailable = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of syslogFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [syslogFields]);

  // Normalize filters (Available)
  const normalizedFiltersAvailable = useMemo(() => {
    return activeFiltersAvailable.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMapAvailable.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldByIdAvailable.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [activeFiltersAvailable, keyMapAvailable, fieldByIdAvailable]);

  // Build predicate (Available)
  const predicateAvailable = useMemo(
    () => buildPredicate<SyslogItem>(normalizedFiltersAvailable, syslogFields),
    [normalizedFiltersAvailable, syslogFields]
  );

  // Build maps for filter normalization (Selected)
  const fieldByIdSelected = useMemo(() => {
    const m = new Map<string, FilterField<SyslogItem>>();
    for (const f of syslogFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [syslogFields]);

  const keyMapSelected = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of syslogFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [syslogFields]);

  // Normalize filters (Selected)
  const normalizedFiltersSelected = useMemo(() => {
    return activeFiltersSelected.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMapSelected.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldByIdSelected.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [activeFiltersSelected, keyMapSelected, fieldByIdSelected]);

  // Build predicate (Selected)
  const predicateSelected = useMemo(
    () => buildPredicate<SyslogItem>(normalizedFiltersSelected, syslogFields),
    [normalizedFiltersSelected, syslogFields]
  );

  // Apply predicates to filter data
  const filteredAvailable = useMemo(
    () => availableItems.filter(predicateAvailable),
    [availableItems, predicateAvailable]
  );

  const filteredSelected = useMemo(
    () => selectedItems.filter(predicateSelected),
    [selectedItems, predicateSelected]
  );

  // Check for changes
  useEffect(() => {
    const sameAvail =
      JSON.stringify(availableItems) === JSON.stringify(initialAvailable);
    const sameSel =
      JSON.stringify(selectedItems) === JSON.stringify(initialSelected);
    setHasChanges(!sameAvail || !sameSel);
  }, [availableItems, selectedItems, initialAvailable, initialSelected]);

  // Load syslog data directly when ctxrId or clusterId is provided (embedded in ReflectorDetails/ClusterDetails)
  useEffect(() => {
    if (isLoading || !isAuthenticated || !tenantId) return;

    // If ctxrId or clusterId is provided, load data directly without showing selector
    if (propCtxrId || clusterId) {
      if (propCtxrId) {
        setCurrentCtxrId(propCtxrId);
        loadSyslogData(propCtxrId);
      } else if (clusterId) {
        // For cluster, we still need a ctxrId for the function signature, but V2 API will use clusterId
        // Use a placeholder or the first instance ID if available
        setCurrentCtxrId("cluster"); // Placeholder, actual loading uses clusterId from prop
        loadSyslogData("cluster"); // Placeholder, actual loading uses clusterId from prop
      }
      return;
    }

    // Only fetch CTX-R list when no ctxrId/clusterId is provided (standalone mode)
    const fetchCTXRList = async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();
        const response = await getCTXRlistGlobal(-1, tenantId, token);
        const dataArray: CtxrItem[] = response.data || [];

        setFullCTXRlist(dataArray);
        const mapped = dataArray.map(
          (item) => `${item.name} (${item.description})`
        );
        setInstanceOptions(mapped);

        // Select first available
        if (mapped.length > 0) {
          const first = mapped[0];
          setSelectedInstance(first);
          const found = dataArray.find(
            (x) => `${x.name} (${x.description})` === first
          );
          if (found) {
            setCurrentCtxrId(found.id);
            await loadSyslogData(found.id);
          }
        }
      } catch (err) {
        console.error("Error fetching CTXR list:", err);
      } finally {
        setIsFetching(false);
      }
    };

    fetchCTXRList();
  }, [isLoading, isAuthenticated, getAccessTokenSilently, tenantId, propCtxrId, clusterId]);

  // Note: syslog data is loaded when instance is selected via handleInstanceSelect
  // or when CTX-R list is first loaded. We don't need a separate effect here
  // to avoid double-loading when currentCtxrId changes from prop updates.

  const handleInstanceSelect = async (instanceStr: string) => {
    // Prevent double calls: if already selected or loading, ignore
    if (selectedInstance === instanceStr || isLoadingDataRef.current) {
      return;
    }

    setSelectedInstance(instanceStr);
    const found = fullCTXRlist.find(
      (x) => `${x.name} (${x.description})` === instanceStr
    );
    if (found) {
      setCurrentCtxrId(found.id);
      await loadSyslogData(found.id);
    }
  };

  const loadSyslogData = async (ctxrIdParam: string) => {
    // Prevent concurrent calls
    if (isLoadingDataRef.current) {
      return;
    }

    try {
      isLoadingDataRef.current = true;
      setIsFetching(true);
      setSyslogError(null);
      const token = await getAccessTokenSilently();

      // Determine V2 mode: use V2 API when inside ReflectorDetails or ClusterDetails pages
      // V2 mode is determined by the presence of propCtxrId or clusterId props
      const isV2Mode = !!(propCtxrId || clusterId);
      
      if (isV2Mode) {
        // V2 API: Load available and selected syslog servers in parallel
        const [respAvailable, respSelected] = await Promise.all([
          // Available syslog servers (excluding those associated with this instance/cluster)
          (clusterId
            ? getAvailableSyslogServersV2(tenantId, token, clusterId, 'cluster')
            : propCtxrId
            ? getAvailableSyslogServersV2(tenantId, token, Number(propCtxrId), 'reflector')
            : Promise.resolve({ data: { data: [] } })
          ).catch((err) => {
            console.error('Error fetching available syslogs:', err);
            return { data: { data: [] } };
          }),
          // Selected syslog servers (for instance or cluster)
          (clusterId
            ? getSelectedSyslogsV2ForCluster(clusterId, tenantId, token)
            : propCtxrId
            ? getSelectedSyslogsV2ForInstance(propCtxrId, tenantId, token)
            : Promise.resolve({ data: { data: [] } })
          ).catch((err) => {
            console.error('Error fetching selected syslogs:', err);
            // If selected endpoint fails or returns 404, it means no syslogs are selected yet
            return { data: { data: [] } };
          }),
        ]);
        
        // V2 API returns paginated response: { total, page, limit, data: [...] }
        const availableSyslogs: SyslogServerResponse[] = respAvailable.data?.data || [];
        
        // Available = map to SyslogItem format
        const dataAvail: SyslogItem[] = availableSyslogs.map((server) => ({
          id: server.id,
          ip: server.ip || "",
          port: server.port || 0,
          description: server.description || "",
          protocol: server.protocol || "udp",
          level: 0, // Default level
        }));
        
        // Selected = map association response to SyslogItem format
        const selectedData: SyslogAssociationResponse[] | SyslogAssociationResponse = respSelected.data?.data || respSelected.data || [];
        const selectedArray: SyslogAssociationResponse[] = Array.isArray(selectedData) ? selectedData : (selectedData ? [selectedData] : []);
        
        // For cluster mode, filter by propagationMode === "cluster"
        // For instance/standalone mode, filter by propagationMode === "standalone"
        const filteredAssociations = clusterId
          ? selectedArray.filter((item) => item.propagationMode === "cluster")
          : selectedArray.filter((item) => item.propagationMode === "standalone");
        
        // Map to SyslogItem format, extracting data from nested syslogServer object
        // Note: 
        // - In cluster mode: response may have both `id` and `associationIds` (array) - one syslog server can have multiple associations
        // - In standalone mode: response has `id` (single number) - one syslog server has one association
        // We normalize both to `associationIds` array format for consistent deletion logic
        const dataSelected: SyslogItem[] = filteredAssociations.map((association) => {
          const syslogServer = association.syslogServer;
          
          // Normalize association IDs: 
          // - Cluster mode: prefer `associationIds` array, but also check for `id` and combine if both exist
          // - Standalone mode: use `id` (single number) converted to array
          let associationIds: number[] = [];
          
          if (association.propagationMode === "cluster") {
            // Cluster mode: use associationIds array, but also include id if present
            if (association.associationIds && association.associationIds.length > 0) {
              associationIds = [...association.associationIds];
            }
            // If id is also present, add it (avoid duplicates)
            if (association.id && !associationIds.includes(association.id)) {
              associationIds.push(association.id);
            }
          } else {
            // Standalone mode: use id (single number) converted to array
            if (association.id) {
              associationIds = [association.id];
            }
          }
          
          const mappedItem = {
            id: syslogServer.id,
            ip: syslogServer.ip || "",
            port: syslogServer.port || 0,
            description: syslogServer.description || "",
            protocol: syslogServer.protocol || "udp",
            level: association.level ?? 0,
            associationIds: associationIds, // Normalized array of association IDs for deletion
            associationId: associationIds[0], // Keep for backward compatibility, use first ID
          };
          return mappedItem;
        });

        setAvailableItems(dataAvail || []);
        setSelectedItems(dataSelected || []);

        setInitialAvailable(JSON.parse(JSON.stringify(dataAvail || [])));
        setInitialSelected(JSON.parse(JSON.stringify(dataSelected || [])));
      } else {
        // V1 API (standalone mode - only when no ctxrId/clusterId props)
        const subscriptionId = 1;

        // Available
        const respAvail = await getAvailableSyslogs(
          subscriptionId,
          ctxrIdParam,
          tenantId,
          token
        );
        const dataAvail = respAvail.data as SyslogItem[];

        // Selected
        const respSelected = await getSelectedSyslogs(
          subscriptionId,
          ctxrIdParam,
          tenantId,
          token
        );
        const dataSelected = respSelected.data as SyslogItem[];

        setAvailableItems(dataAvail || []);
        setSelectedItems(dataSelected || []);

        setInitialAvailable(JSON.parse(JSON.stringify(dataAvail || [])));
        setInitialSelected(JSON.parse(JSON.stringify(dataSelected || [])));
      }

      // Clear selection sets
      setSelectedAvailableIds(new Set());
      setSelectedSelectedIds(new Set());
    } catch (error) {
      console.error("Error loading syslog data =>", error);
      setSyslogError("Failed to load syslog data. Please try again.");
    } finally {
      setIsFetching(false);
      isLoadingDataRef.current = false;
    }
  };

  // Toggle checkbox
  const toggleAvailableId = (id: number) => {
    setSelectedAvailableIds((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const toggleSelectedId = (id: number) => {
    setSelectedSelectedIds((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  // Move arrows
  const handleMoveToSelected = () => {
    if (selectedAvailableIds.size === 0) return;
    const moving = availableItems.filter((it) =>
      selectedAvailableIds.has(it.id)
    );
    const remainAvail = availableItems.filter(
      (it) => !selectedAvailableIds.has(it.id)
    );
    const mergedSelected = [...selectedItems, ...moving];

    setAvailableItems(remainAvail);
    setSelectedItems(mergedSelected);
    setSelectedAvailableIds(new Set());
  };

  const handleMoveToAvailable = () => {
    if (selectedSelectedIds.size === 0) return;
    
    // Just move in UI - deletion will happen on Save
    // This works for both V1 and V2 modes
    const moving = selectedItems.filter((it) => selectedSelectedIds.has(it.id));
    const remainSelected = selectedItems.filter(
      (it) => !selectedSelectedIds.has(it.id)
    );
    const newAvailable = [...availableItems, ...moving];

    setAvailableItems(newAvailable);
    setSelectedItems(remainSelected);
    setSelectedSelectedIds(new Set());
  };

  // Level change
  const handleLevelChange = (row: SyslogItem, newLevelStr: string) => {
    const LOG_LEVELS = [
      "Emergency",
      "Alert",
      "Critical",
      "Error",
      "Warning",
      "Notice",
      "Informational",
      "Debug",
    ];
    const newLevelIndex = LOG_LEVELS.indexOf(newLevelStr);
    setSelectedItems((prev) =>
      prev.map((it) =>
        it.id === row.id ? { ...it, level: newLevelIndex } : it
      )
    );
  };

  // Alerts
  const [undoAlertOpen, setUndoAlertOpen] = useState(false);

  const doActualUndo = () => {
    setAvailableItems(JSON.parse(JSON.stringify(initialAvailable)));
    setSelectedItems(JSON.parse(JSON.stringify(initialSelected)));
    setSelectedAvailableIds(new Set());
    setSelectedSelectedIds(new Set());
  };

  const handleUndo = () => {
    if (!hasChanges) return;
    setUndoAlertOpen(true);
  };

  const confirmUndo = () => {
    setUndoAlertOpen(false);
    doActualUndo();
    setSuccessAlertOpen(true);
    setAlertTitle("Discarded");
    setAlertDesc("All changes have been discarded.");
  };

  const cancelUndo = () => {
    setUndoAlertOpen(false);
  };

  // Save
  const [saveAlertOpen, setSaveAlertOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = () => {
    if (!hasChanges || !currentCtxrId) return;
    setSaveAlertOpen(true);
  };

  const confirmSave = async () => {
    if (!currentCtxrId) return;

    setSaveAlertOpen(false);
    setIsSaving(true);

    try {
      const token = await getAccessTokenSilently();
      
      // Determine V2 mode: use V2 API when inside ReflectorDetails or ClusterDetails pages
      const isV2Mode = !!(propCtxrId || clusterId);
      
      if (isV2Mode) {
        // V2 API: Handle both deletions and creations
        
        // 1. Delete associations for items that were moved from Selected to Available
        // Compare initialSelected with current selectedItems to find items to delete
        const itemsToDelete = initialSelected.filter(
          (initialItem) => !selectedItems.some((currentItem) => currentItem.id === initialItem.id)
        );
        
        // Delete associations for removed items
        if (itemsToDelete.length > 0) {
          const mode = clusterId ? 'cluster' : 'standalone';
          
          for (const syslog of itemsToDelete) {
            // Get association IDs - prefer associationIds array, fallback to associationId
            const associationIdsToDelete = syslog.associationIds && syslog.associationIds.length > 0
              ? syslog.associationIds
              : syslog.associationId
              ? [syslog.associationId]
              : [];
            
            if (associationIdsToDelete.length === 0) {
              continue;
            }
            
            if (mode === 'cluster') {
              // Cluster mode: single request with clusterId and all associationIds
              await deleteSyslogAssociationV2(
                undefined,
                mode,
                tenantId,
                token,
                undefined,
                clusterId,
                associationIdsToDelete
              );
            } else {
              // Standalone mode: delete each association individually
              for (const associationId of associationIdsToDelete) {
                await deleteSyslogAssociationV2(
                  associationId,
                  mode,
                  tenantId,
                  token,
                  propCtxrId ? Number(propCtxrId) : undefined,
                  undefined
                );
              }
            }
          }
        }
        
        // 2. Create associations for items that were moved from Available to Selected
        // Compare initialSelected with current selectedItems to find new items
        const itemsToCreate = selectedItems.filter(
          (currentItem) => !initialSelected.some((initialItem) => initialItem.id === currentItem.id)
        );
        
        // 3. Update associations for items that stayed in Selected but had level changes
        // Compare initialSelected with current selectedItems to find items with level changes
        const itemsToUpdate = selectedItems.filter((currentItem) => {
          const initialItem = initialSelected.find((item) => item.id === currentItem.id);
          if (!initialItem) return false; // Item is new (handled in itemsToCreate)
          
          // Check if level changed
          const initialLevel = initialItem.level ?? 0;
          const currentLevel = currentItem.level ?? 0;
          return initialLevel !== currentLevel;
        });
        
        // Update associations for items with level changes
        if (itemsToUpdate.length > 0) {
          for (const syslog of itemsToUpdate) {
            // Get association IDs - prefer associationIds array, fallback to associationId
            const associationIdsToUpdate = syslog.associationIds && syslog.associationIds.length > 0
              ? syslog.associationIds
              : syslog.associationId
              ? [syslog.associationId]
              : [];
            
            if (associationIdsToUpdate.length === 0) {
              continue;
            }
            
            // Update each association ID with the new level
            for (const associationId of associationIdsToUpdate) {
              await updateSyslogAssociation(
                associationId,
                tenantId,
                token,
                syslog.level ?? 0
              );
            }
          }
        }
        
        // 4. Create associations for new items
        if (itemsToCreate.length > 0) {
          // Level mapping: 0=Emergency, 1=Alert, 2=Critical, 3=Error, 4=Warning, 5=Notice, 6=Informational, 7=Debug
          if (clusterId) {
            // Cluster mode: use clusterId in payload
            for (const syslog of itemsToCreate) {
              await createSyslogAssociationForCluster(tenantId, token, {
                syslogServerId: syslog.id,
                clusterId: clusterId,
                level: syslog.level ?? 0,
              });
            }
          } else if (propCtxrId) {
            // Instance mode: use reflectorInstanceId in payload
            const instanceId = Number(propCtxrId);
            for (const syslog of itemsToCreate) {
              await createSyslogAssociationForInstance(tenantId, token, {
                syslogServerId: syslog.id,
                reflectorInstanceId: instanceId,
                level: syslog.level ?? 0,
              });
            }
          }
        }
      } else {
        // V1 API (standalone mode)
        const subscriptionId = 1;
        await saveSyslogs(
          subscriptionId,
          currentCtxrId,
          tenantId,
          token,
          selectedItems
        );
      }
      
      await loadSyslogData(currentCtxrId);

      // Success => success alert
      setSuccessAlertOpen(true);
      setAlertTitle("Saved!");
      setAlertDesc("Your changes have been saved successfully.");
    } catch (err) {
      console.error("Error saving syslogs =>", err);
      setErrorAlertOpen(true);
      setErrorTitle("Error on save");
      setErrorDesc("Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const cancelSave = () => {
    setSaveAlertOpen(false);
  };

  // Success alert
  const [successAlertOpen, setSuccessAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDesc, setAlertDesc] = useState("");

  const closeSuccessAlert = () => {
    setSuccessAlertOpen(false);
  };

  // Error alert
  const [errorAlertOpen, setErrorAlertOpen] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorDesc, setErrorDesc] = useState("");

  const closeErrorAlert = () => {
    setErrorAlertOpen(false);
  };

  // Columns
  const availableColumns = getAvailableColumns(
    selectedAvailableIds,
    toggleAvailableId
  );
  const selectedColumns = getSelectedColumns(
    selectedSelectedIds,
    toggleSelectedId,
    handleLevelChange
  );

  // Standalone mode: loading or empty instance list (no ctxrId/clusterId provided)
  if (!propCtxrId && !clusterId && instanceOptions.length === 0 && !currentCtxrId) {
    return (
      <GenericCard isCollapsible={false}>
        <DataStateView
          loading={isFetching}
          error={null}
          isEmpty={!isFetching}
          loadingContent={
            <div className="p-4 space-y-3">
              <SkeletonLine w={280} h={20} className="rounded" />
              <SkeletonLine w={320} h={40} className="rounded" />
            </div>
          }
          errorTitle=""
          emptyTitle="No CTX-R instance available"
          emptyDescription="Select a CTX-R instance to configure syslog."
        >
          <div className="p-4" />
        </DataStateView>
      </GenericCard>
    );
  }

  return (
    <div className="relative">
      {/* CTX-R Instance Selector - Only shown in standalone mode (when ctxrId/clusterId not provided) */}
      {!propCtxrId && !clusterId && (
        <div className="mb-4">
          <GenericCard isCollapsible={false}>
            <div className="p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
                <div className="flex flex-col gap-3 w-full sm:w-auto sm:flex-row sm:items-end sm:gap-0 sm:space-x-4">
                  <div className="flex flex-col w-full sm:w-auto">
                    {isFetching && instanceOptions.length === 0 ? (
                      <SkeletonLine w={300} h={16} />
                    ) : (
                      <GenericDropdown
                        items={instanceOptions}
                        value={selectedInstance}
                        onSelect={handleInstanceSelect}
                        minWidthClass="min-w-full sm:min-w-[300px]"
                        widthClass="w-full sm:w-60"
                        label="CTX-R instance"
                        dropdownMaxHeightClass="max-h-48"
                        disabled={instanceOptions.length === 0}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </GenericCard>
        </div>
      )}

      <GenericCard isCollapsible={false}>
        <div className="flex flex-col gap-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Syslog</h2>
          </div>

          {/* Error state with retry (skeleton-only loading below) */}
          {syslogError && (
            <ErrorStateView
              title="Failed to load syslog"
              message={syslogError}
              onRetry={() => {
                setSyslogError(null);
                if (currentCtxrId) loadSyslogData(currentCtxrId);
              }}
            />
          )}

          {/* CONTENT: 2 tables + Filters (skeleton when isFetching) */}
          {!syslogError && (
          <div className="flex flex-col md:flex-row gap-6 md:min-w-0">
            {/* LEFT SIDE: Available */}
            <div className="flex-1 min-w-0">
              <GenericCard isCollapsible={false} isLoading={isLoading} className="flex flex-col">
                <div className="flex flex-col gap-2 mb-4 p-4">
                  <h3 className="text-semantic-text-primary font-medium">
                    Available
                  </h3>
                  {/* Filter with GenericSearchbar */}
                  <GenericSearchbar
                    placeholder="Find by attribute or value (e.g., IP: 192.168.1.1)"
                    suggestedKeys={suggestedKeys}
                    onFiltersChange={setActiveFiltersAvailable}
                    liveFiltering={true}
                    debounceMs={200}
                  />
                </div>
                {isFetching ? (
                  <div className="p-4">
                    <SkeletonTableRows
                      rows={5}
                      cols={4}
                      colWidths={["40px", "150px", "100px", "200px"]}
                      rowHeight={15}
                      rowGapClassName="space-y-3"
                    />
                  </div>
                ) : (
                  <div className="px-4 pb-4 min-w-0">
                    <GenericTable
                      columns={availableColumns}
                      data={filteredAvailable}
                      initialRowsPerPage={getInitialRowsPerPage(filteredAvailable.length)}
                      rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
                      tableClassName="bg-semantic-bg-primary"
                      width="100%"
                      rowKey={(row) => row.id}
                      onRowClick={(row) => toggleAvailableId(row.id)}
                      selectedRowKeys={new Set(selectedAvailableIds)}
                      maxTableBodyHeight={getTableBodyMaxHeight(SYSLOG_PICKER_CHROME_PX)}
                    />
                  </div>
                )}
              </GenericCard>
            </div>

            {/* Arrows - Centered (horizontal on mobile, vertical from md+) */}
            <div className="flex flex-row md:flex-col items-center justify-center gap-4 flex-shrink-0">
              <GenericButton
                label=""
                iconLeftName="arrowRight"
                variant="secondary"
                shape="ghost"
                size="md"
                onClick={handleMoveToSelected}
                disabled={selectedAvailableIds.size === 0}
                aria-label="Move to Selected"
              />
              <GenericButton
                label=""
                iconLeftName="arrowLeft"
                variant="secondary"
                shape="ghost"
                size="md"
                onClick={handleMoveToAvailable}
                disabled={selectedSelectedIds.size === 0}
                aria-label="Move to Available"
              />
            </div>

            {/* RIGHT SIDE: Selected */}
            <div className="flex-1 min-w-0">
              <GenericCard isCollapsible={false} isLoading={isLoading} className="flex flex-col">
                <div className="flex flex-col gap-2 mb-4 p-4">
                  <h3 className="text-semantic-text-primary font-medium">
                    Selected
                  </h3>
                  {/* Filter with GenericSearchbar */}
                  <GenericSearchbar
                    placeholder="Find by attribute or value (e.g., IP: 192.168.1.1)"
                    suggestedKeys={suggestedKeys}
                    onFiltersChange={setActiveFiltersSelected}
                    liveFiltering={true}
                    debounceMs={200}
                  />
                </div>

                {isFetching ? (
                  <div className="p-4">
                    <SkeletonTableRows
                      rows={5}
                      cols={5}
                      colWidths={["40px", "150px", "100px", "200px", "140px"]}
                      rowHeight={15}
                      rowGapClassName="space-y-3"
                    />
                  </div>
                ) : (
                  <div className="px-4 pb-4 min-w-0">
                    <GenericTable
                      columns={selectedColumns}
                      data={filteredSelected}
                      initialRowsPerPage={getInitialRowsPerPage(filteredSelected.length)}
                      rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
                      tableClassName="bg-semantic-bg-primary"
                      width="100%"
                      rowKey={(row) => row.id}
                      onRowClick={(row) => toggleSelectedId(row.id)}
                      selectedRowKeys={new Set(selectedSelectedIds)}
                      maxTableBodyHeight={getTableBodyMaxHeight(SYSLOG_PICKER_CHROME_PX)}
                    />
                  </div>
                )}
              </GenericCard>
            </div>
          </div>
          )}

          {/* BUTTONS: Undo & Save (hidden when error so user retries first) */}
          {!syslogError && (
          <div className="mt-4 flex items-center justify-end gap-4">
            <GenericButton
              label="Undo"
              variant="secondary"
              size="md"
              onClick={handleUndo}
              disabled={!hasChanges}
            />
            <GenericButton
              label={isSaving ? "Saving..." : "Save"}
              variant="brand"
              size="md"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
            />
          </div>
          )}
        </div>
      </GenericCard>

      {/* ALERTS */}
      {/* 1) Confirm Undo */}
      <GenericAlert
        isOpen={undoAlertOpen}
        onClose={cancelUndo}
        onConfirm={confirmUndo}
        title="Unsaved Changes"
        description="Are you sure you want to discard the changes?"
        cancelLabel="No"
        confirmLabel="Yes"
        variant="warning"
      />

      {/* 2) Confirm Save */}
      <GenericAlert
        isOpen={saveAlertOpen}
        onClose={cancelSave}
        onConfirm={confirmSave}
        title="Save Changes"
        description="Are you sure you want to save the changes?"
        cancelLabel="No"
        confirmLabel="Yes"
        variant="warning"
      />

      {/* 3) Success Alert */}
      <GenericAlert
        isOpen={successAlertOpen}
        onClose={closeSuccessAlert}
        onConfirm={closeSuccessAlert}
        title={alertTitle}
        description={alertDesc}
        confirmLabel="OK"
        variant="success"
        hideCancel={true}
      />

      {/* 4) Error Alert */}
      <GenericAlert
        isOpen={errorAlertOpen}
        onClose={closeErrorAlert}
        onConfirm={closeErrorAlert}
        title={errorTitle}
        description={errorDesc}
        confirmLabel="OK"
        variant="error"
        hideCancel={true}
      />
    </div>
  );
}

export default SyslogPage;

